import * as React from "react";
import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "@/ui/components/ui/avatar";
import { Button } from "@/ui/components/ui/button";
import { ConfirmationModal } from "@/ui/components/ui/confirmation-modal";
import ReactCrop, { type Crop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  resizeImageIfNeeded,
  MIN_IMAGE_DIMENSION,
} from "@/lib/imageUtils";

/**
 * Crops an image based on ReactCrop coordinates and returns a File object
 * @param imageSrc - URL of the image (from canvas or blob URL)
 * @param crop - Crop dimensions from ReactCrop
 * @param originalFile - Original File object (to preserve MIME type)
 * @param displayedWidth - Width of the image as displayed in the modal (CSS-scaled)
 * @param displayedHeight - Height of the image as displayed in the modal (CSS-scaled)
 * @returns Cropped image as File object
 */
async function getCroppedImg(
  imageSrc: string,
  crop: Crop,
  originalFile: File,
  displayedWidth: number,
  displayedHeight: number
): Promise<File> {
  const image = new Image();
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Failed to get canvas context");
  }

  return new Promise((resolve, reject) => {
    image.onload = () => {
      // Calculate scale factors between natural and displayed image dimensions
      // ReactCrop returns coordinates relative to the DISPLAYED size, not natural size
      const scaleX = image.naturalWidth / displayedWidth;
      const scaleY = image.naturalHeight / displayedHeight;

      // Get crop values
      let x = crop.x || 0;
      let y = crop.y || 0;
      let width = crop.width || (crop.unit === "%" ? 100 : displayedWidth);
      let height = crop.height || (crop.unit === "%" ? 100 : displayedHeight);

      // Convert coordinates to natural image pixels based on unit
      if (crop.unit === "%") {
        // Percentage: convert directly to natural pixels
        x = (x / 100) * image.naturalWidth;
        y = (y / 100) * image.naturalHeight;
        width = (width / 100) * image.naturalWidth;
        height = (height / 100) * image.naturalHeight;
      } else {
        // Pixels: scale from displayed size to natural size
        x = x * scaleX;
        y = y * scaleY;
        width = width * scaleX;
        height = height * scaleY;
      }

      // Round to integers (canvas requires integer pixels)
      x = Math.round(x);
      y = Math.round(y);
      width = Math.round(width);
      height = Math.round(height);

      // Validate minimum dimensions
      if (width < 1 || height < 1) {
        reject(new Error("Crop dimensions too small"));
        return;
      }

      // Clamp to image bounds to prevent drawing outside source
      x = Math.max(0, Math.min(x, image.naturalWidth - 1));
      y = Math.max(0, Math.min(y, image.naturalHeight - 1));
      width = Math.min(width, image.naturalWidth - x);
      height = Math.min(height, image.naturalHeight - y);

      // Set canvas size to cropped dimensions
      canvas.width = width;
      canvas.height = height;

      // Draw cropped image onto canvas
      ctx.drawImage(image, x, y, width, height, 0, 0, width, height);

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Failed to create blob from canvas"));
          return;
        }

        // Create File from blob, preserving original filename and type
        const croppedFile = new File([blob], originalFile.name, {
          type: originalFile.type,
          lastModified: Date.now(),
        });

        resolve(croppedFile);
      }, originalFile.type);
    };

    image.onerror = () => {
      reject(new Error("Failed to load image for cropping"));
    };

    image.src = imageSrc;
  });
}

export interface AvatarUploadSectionProps {
  avatarUrl?: string;
  isLoading: boolean;
  uploadProgress: number; // 0-100
  initials: string;
  onUpload: (file: File) => Promise<void>;
  onDelete: () => Promise<void>;
}

/**
 * AvatarUploadSection Component
 *
 * Handles avatar upload with image preview, cropping, and deletion.
 * Features:
 * - File picker with validation (JPEG, PNG, WebP)
 * - Size validation (max 5MB)
 * - Auto-resize for images exceeding 2000x2000 pixels (mobile photo support)
 * - Minimum dimension validation (200x200 pixels)
 * - Image crop functionality
 * - Upload progress display
 * - Delete confirmation modal
 *
 * @example
 * ```tsx
 * <AvatarUploadSection
 *   avatarUrl="/path/to/avatar.jpg"
 *   isLoading={false}
 *   uploadProgress={50}
 *   initials="JD"
 *   onUpload={handleUpload}
 *   onDelete={handleDelete}
 * />
 * ```
 */
export function AvatarUploadSection({
  avatarUrl,
  isLoading,
  uploadProgress,
  initials,
  onUpload,
  onDelete,
}: AvatarUploadSectionProps) {
  const { t } = useTranslation();
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [crop, setCrop] = useState<Crop>({
    x: 25,
    y: 25,
    width: 50,
    height: 50,
    unit: "%",
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [validationError, setValidationError] = useState<string>("");
  const [isCropping, setIsCropping] = useState(false);
  const [wasImageResized, setWasImageResized] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cropImageRef = useRef<HTMLImageElement>(null);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setValidationError("");
    setWasImageResized(false);

    // Validate file type
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setValidationError(t("profile.avatar.invalidType"));
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setValidationError(t("profile.avatar.invalidSize"));
      return;
    }

    // Process image: resize if needed and validate dimensions
    try {
      const {
        file: processedFile,
        wasResized,
        width,
        height,
      } = await resizeImageIfNeeded(file);

      // Validate minimum dimensions (after potential resize)
      if (width < MIN_IMAGE_DIMENSION || height < MIN_IMAGE_DIMENSION) {
        setValidationError(t("profile.avatar.imageTooSmall"));
        return;
      }

      // Track if resize occurred (for optional info message)
      setWasImageResized(wasResized);

      // Create preview URL for crop modal
      const objectUrl = URL.createObjectURL(processedFile);

      // Show crop modal with processed file
      setSelectedFile(processedFile);
      setPreviewUrl(objectUrl);
      setShowCropModal(true);
    } catch (error) {
      console.error("Image processing error:", error);
      setValidationError(t("profile.avatar.resizeError"));
    }
  };

  const handleCropConfirm = async () => {
    if (!selectedFile || !crop || !crop.width || !crop.height) return;
    if (!cropImageRef.current) return;

    // Get displayed image dimensions (CSS-scaled size in the modal)
    const displayedWidth = cropImageRef.current.width;
    const displayedHeight = cropImageRef.current.height;

    setIsCropping(true);
    try {
      // Step 1: Apply crop to create cropped file (fast operation, 50-200ms)
      const croppedFile = await getCroppedImg(
        previewUrl,
        crop,
        selectedFile,
        displayedWidth,
        displayedHeight
      );

      // Step 2: Close modal and cleanup BEFORE starting upload
      setShowCropModal(false);
      setSelectedFile(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl("");
      setCrop({
        x: 25,
        y: 25,
        width: 50,
        height: 50,
        unit: "%",
      });

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setIsCropping(false); // Crop processing done
      setWasImageResized(false); // Reset resize state after upload

      // Step 3: Start upload (modal already closed, progress shows in ProfilePage)
      await onUpload(croppedFile);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      setValidationError(
        `${t("profile.avatar.cropError")}: ${errorMessage}`
      );
      console.error("Crop error:", error);
      setIsCropping(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl("");
    setCrop({
      x: 25,
      y: 25,
      width: 50,
      height: 50,
      unit: "%",
    });
    setWasImageResized(false); // Reset resize state on cancel

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    try {
      await onDelete();
      setShowDeleteConfirm(false);
    } catch (error) {
      setValidationError(t("profile.avatar.uploadError"));
    }
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <Avatar
        src={avatarUrl}
        editable={true}
        onEditClick={handleAvatarClick}
        size="xl"
        fallback={<span className="text-lg">{initials}</span>}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        aria-label="Upload avatar image"
      />

      {/* Validation Error */}
      {validationError && (
        <p className="text-sm text-danger text-center max-w-[21.4375rem]">
          {validationError}
        </p>
      )}

      {/* Resize Info Message - shown when image was auto-resized */}
      {wasImageResized && !validationError && showCropModal && (
        <p className="text-sm text-gray-500 text-center max-w-[21.4375rem]">
          {t("profile.avatar.imageResized")}
        </p>
      )}

      {/* Upload Progress */}
      {uploadProgress > 0 && uploadProgress < 100 && (
        <div className="w-full max-w-[12.5rem]">
          <div className="h-[0.125rem] bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-red transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="text-sm text-gray-500 text-center mt-1">
            {t("profile.avatar.uploading", { progress: uploadProgress })}
          </p>
        </div>
      )}

      {/* Remove Photo Button */}
      {/* {avatarUrl && !isLoading && (
        <Button
          variant="primary-dark"
          size="primary-dark"
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isLoading}
        >
          {t("profile.avatar.remove")}
        </Button>
      )} */}

      {/* Crop Modal */}
      {showCropModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            // Close modal when clicking on backdrop (outside the modal content)
            if (e.target === e.currentTarget && !isCropping) {
              handleCropCancel();
            }
          }}
        >
          <div className="bg-white p-6 rounded-lg max-w-2xl w-full">
            <h3 className="text-lg font-semibold mb-4 font-montserrat">
              {t("profile.avatar.cropImage")}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {t("profile.avatar.cropHint")}
            </p>
            <div className="max-h-[28.125rem] overflow-auto">
              <ReactCrop
                crop={crop}
                onChange={setCrop}
                aspect={1} // 1:1 aspect ratio for square avatar
                circularCrop={false}
                disabled={isCropping}
              >
                <img ref={cropImageRef} src={previewUrl} alt="Preview" />
              </ReactCrop>
            </div>

            {/* Loading indicator during crop processing */}
            {isCropping && (
              <div className="flex items-center justify-center gap-2 mt-4 p-2 bg-gray-50 rounded">
                <div className="animate-spin h-4 w-4 border-2 border-brand-red border-t-transparent rounded-full" />
                <p className="text-sm text-gray-600">
                  {t("profile.avatar.processing")}
                </p>
              </div>
            )}

            <div className="flex gap-4 mt-6">
              <Button
                variant="secondary"
                size="primary-dark"
                onClick={handleCropCancel}
                disabled={isCropping || isLoading}
              >
                {t("profile.avatar.cancelButton")}
              </Button>
              <Button
                variant="primary-dark"
                size="primary-dark"
                onClick={handleCropConfirm}
                disabled={isCropping || isLoading}
              >
                {isCropping
                  ? t("profile.avatar.processing")
                  : t("profile.avatar.uploadButton")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="max-w-[21.4375rem] w-full">
            <ConfirmationModal
              message={t("profile.avatar.removeConfirm")}
              confirmText={t("profile.avatar.remove").toUpperCase()}
              cancelText={t("common.cancel").toUpperCase()}
              onConfirm={handleDelete}
              onCancel={() => setShowDeleteConfirm(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
